<template>
  <div class="tech-comment-collapse">
    <div
      v-if="canEdit && parsedComments.length < 1"
      class="section-desc margin-bottom"
    >
      {{ $t('tenantProjects.submission.comment.addTechCommentHint') }}
    </div>
    <Collapse
      v-for="(section, index) in parsedComments"
      :id="`tech-comment-collapse-${index}`"
      :key="section.id"
      :enableExpandByClickingTitle="!isEdit[section.itemId]"
      :titleContainerClass="{'is-editing': isEdit[section.itemId]}"
    >
      <template slot="title">
        <div class="section-title-box">
          <div
            v-if="isEdit[section.itemId]"
            class="section-title-editing"
          >
            <input
              v-model="section.displayOrder"
              :placeholder="$t('tenantProjects.submission.comment.section.number')"
              class="section-title-order"
              type="text"
              @change="event => changeSection(section, 'displayOrder', event.target.value)"
            >
            <input
              v-model="section.value"
              :placeholder="$t('tenantProjects.submission.comment.section.title')"
              class="section-title-text"
              type="text"
              @change="event => changeSection(section, 'value', event.target.value)"
              @blur="onBlurSave(section)"
            >
          </div>
          <template v-else>
            <span v-show="section.displayOrder">{{ `${section.displayOrder}.` }}</span>
            <span class="section-title">{{ section.value }}</span>
          </template>
          <div
            v-if="section.lastActionText"
            class="update-history__text"
          >
            <span>{{ section.lastActionText }}</span>
          </div>
        </div>
      </template>
      <div
        v-if="canEdit && !isEdit[section.itemId]"
        slot="additional"
        class="additional-container"
        :class="{hidden: !canEdit}"
      >
        <DottedMenu>
          <a @click="editSection(section, index)">{{ $t('action.edit') }}</a>
          <a @click="addSection(section, index)">{{ $t('action.add') }}</a>
          <a @click="openDelModalForSection(section, index)">{{ $t('action.delete') }}</a>
        </DottedMenu>
      </div>
      <template slot="content">
        <div
          v-for="(line, i) in section.sectionContent"
          :key="i"
          class="content-box"
        >
          <div
            v-show="!isEdit[line.itemId]"
            class="section-list-item"
            :class="{'with-options': canEdit}"
          >
            <div>
              <div
                v-show="showSeq(line)"
                class="section-list-item__number"
              >
                {{ `${showSeq(line)}.` }}
              </div>
              <pre class="section-list-item__value">{{ line.value }}</pre>
            </div>
            <div
              v-if="line.lastActionText"
              class="update-history__text"
            >
              <span>{{ line.lastActionText }}</span>
            </div>
          </div>
          <div
            v-show="isEdit[line.itemId]"
            class="tech-comment-text"
          >
            <textarea
              :ref="getLineRefName(index, i)"
              v-autosize
              v-hkldinput
              :placeholder="$t('tenantProjects.submission.comment.section.content')"
              type="text"
              :value="line.value || ''"
              @change="event => changeLine(section, line, event.target.value)"
              @focusout="onBlurSave(line)"
            />
            <div
              v-if="line.lastActionText"
              class="update-history__text"
            >
              <span>{{ line.lastActionText }}</span>
            </div>
          </div>
          <div
            v-if="canEdit && !isEdit[line.itemId]"
            class="tech-comment-accordion-menu"
          >
            <DottedMenu>
              <a @click="editLine(line, index, i)">{{ $t('action.edit') }}</a>
              <a @click="addLine(section, i)">{{ $t('action.add') }}</a>
              <a @click="openDelModalForLine(section, line)">{{ $t('action.delete') }}</a>
            </DottedMenu>
          </div>
        </div>
      </template>
    </Collapse>
    <ActionConfirmModel
      ref="deleteCommentModal"
      confirmTitle="deleteConfirmMsg"
      confirmBtnName="action.delete"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import { mapMutations } from 'vuex'

import ActionConfirmModel from '@/controls/ActionConfirmModal'
import Collapse from '@/controls/Collapse'
import DottedMenu from '@/controls/DottedMenu'
import TechCommentsMixin from './TechCommentsMixin'

import EventBus from '@/utils/eventBus'
import mutationTypes from '@/store/mutation-types'
import { CommentObject, CommentActionTypes } from './consts'
import { utils as formUtil } from '@/plugin/InputChange'
import { techCommentLineTypes } from '@/utils/locale'
import { createCustomSection, updateSectionSecondaryOrder, isGuideLineSection, buildLastActionText, sortTechComments } from '@/utils/tenantProjectGuidelineUtils'

export default {
  name: 'TechCommentsGuidelines',
  components: {
    ActionConfirmModel,
    Collapse,
    DottedMenu,
  },
  mixins: [TechCommentsMixin],
  data () {
    return {
      isEdit: {},
      suppressAutosave: false,
      suppressAutosaveTimeout: null,
    }
  },
  computed: {
    sortedComments () {
      return sortTechComments(this.ownedComments)
    },
    parsedComments () {
      const filterFn = (item) => {
        return (rec) => {
          const matchLine = rec.guidelineId === item.itemId && !rec.isNewRec
          const matchDate = this.isPic || !this.submittedDate || (new Date(rec.updatedAt) <= new Date(this.submittedDate))
          return matchLine && matchDate
        }
      }
      const comments = _.cloneDeep(this.sortedComments)
      return comments.map(section => {
        const refHistory = this.activeSubmission.actionHistory.find(filterFn(section))
        section.lastActionText = buildLastActionText(refHistory)
        const secContents = section.sectionContent || []
        secContents.forEach((line) => {
          const refHistory = _.orderBy(this.activeSubmission.actionHistory, ['updatedAt'], ['desc']).find(filterFn(line))
          line.lastActionText = buildLastActionText(refHistory)
        })
        return section
      })
    },
  },
  async mounted () {
    EventBus.$on('disable-tech-comments-edit', this.turnAllEditToView)
    EventBus.$on('suppress-submission-autosave', this.onSuppressAutosave)
    EventBus.$on('discard-submission-edits', this.onDiscardSubmissionEdits)
  },
  destroyed () {
    clearTimeout(this.suppressAutosaveTimeout)
    EventBus.$off('disable-tech-comments-edit', this.turnAllEditToView)
    EventBus.$off('suppress-submission-autosave', this.onSuppressAutosave)
    EventBus.$off('discard-submission-edits', this.onDiscardSubmissionEdits)
  },
  methods: {
    ...mapMutations('tenantProject', {
      updateFieldById: mutationTypes.TP_UPDATE_TECH_COMMENTS_BY_ID,
    }),
    onSuppressAutosave () {
      clearTimeout(this.suppressAutosaveTimeout)
      this.suppressAutosave = true
      // Navigation blur occurs in this mousedown event cycle. If there is no
      // focused editor, expire the one-shot flag before any future edit.
      this.suppressAutosaveTimeout = setTimeout(() => {
        this.suppressAutosave = false
        this.suppressAutosaveTimeout = null
      }, 0)
    },
    onDiscardSubmissionEdits () {
      this.onSuppressAutosave()
      this.turnAllEditToView()
    },
    editSection (section, secIndex) {
      this.turnAllEditToView()
      this.$set(this.isEdit, section.itemId, true)
    },
    async addSection (originSection, originIndex) {
      let comments = _.cloneDeep(this.sortedComments)
      const section = await createCustomSection(originSection)
      comments.splice(originIndex + 1, 0, section)
      comments = updateSectionSecondaryOrder(comments, { originSection, originIndex })
      this.updateFieldByPath({path: ['submissions', this.activeSubmissionIndex, 'techComment', 'comments'], newValue: comments})
      this.logSubmissionAction({
        actionType: CommentActionTypes.ADD,
        commentObject: CommentObject.COMMENT,
        guidelineId: section.itemId,
      })
      const newLine = await this.addTechCommentLine({section, line: { value: '' }})
      this.turnAllEditToView()
      this.$set(this.isEdit, section.itemId, true)
      this.$set(this.isEdit, newLine.itemId, false)
    },
    logRemoveSubActionForGuideLineSection (section) {
      section.sectionContent.forEach(item => {
        this.removeSubmissionAction({
          submissionIdx: this.activeSubmissionIndex,
          actionType: CommentActionTypes.CHECK,
          guidelineId: item.itemId,
        })
      })
    },
    deleteSection (originSection, originIndex) {
      const comments = _.cloneDeep(this.sortedComments)
      if (isGuideLineSection(originSection)) {
        this.logRemoveSubActionForGuideLineSection(originSection)
      }
      comments.splice(originIndex, 1)
      this.updateFieldByPath({path: ['submissions', this.activeSubmissionIndex, 'techComment', 'comments'], newValue: comments})
      this.logSubmissionAction({
        actionType: CommentActionTypes.DELETE,
        commentObject: CommentObject.COMMENT,
        guidelineId: originSection.itemId,
      })
    },
    openDelModalForSection (section, index) {
      const confirmAction = async (section, index) => {
        this.deleteSection(section, index)
        await this.updateCurrentTenantSubmission({})
        this.loadTenantProject(this.$router.currentRoute.params.id)
      }
      this.$refs.deleteCommentModal.open(confirmAction, [section, index])
    },
    getSectionPath (secIndex, targetKey) {
      return ['submissions', this.activeSubmissionIndex, 'techComment', 'comments', secIndex, targetKey]
    },
    changeSection (section, attributeName, newValue) {
      this.updateFieldById({
        submissionId: this.activeSubmission.id,
        sectionId: section.id,
        attributeName,
        newValue,
      })
    },
    getLineRefName (secIndex, lineIndex) {
      return `section${secIndex}-line${lineIndex}`
    },
    turnAllEditToView () {
      Object.keys(this.isEdit).forEach(itemId => this.isEdit[itemId] && this.$set(this.isEdit, itemId, false))
    },
    enableLineEdit (item) {
      this.turnAllEditToView()
      this.$set(this.isEdit, item.itemId, true)
    },
    editLine (line, secIndex, lineIndex) {
      this.enableLineEdit(line)
      this.$nextTick(() => {
        const refName = this.getLineRefName(secIndex, lineIndex)
        this.$refs[refName][0].focus()
      })
    },
    async addLine (section, lineIndex) {
      const newLine = await this.addTechCommentLine({section, line: { value: '' }, indexBefore: lineIndex})
      this.enableLineEdit(newLine)
    },
    deleteLine (section, line) {
      this.removeTechCommentLine({section, line})
    },
    changeLine (section, line, newValue) {
      this.updateFieldById({
        submissionId: this.activeSubmission.id,
        sectionId: section.id,
        lineId: line.id,
        attributeName: 'value',
        newValue,
      })
    },
    getPath (secIndex, lineIndex) {
      return ['submissions', this.activeSubmissionIndex, 'techComment', 'comments', secIndex, 'sectionContent', lineIndex, 'value']
    },
    isNewRecord (item) {
      if (!item) return true
      return this.activeSubmission.actionHistory
        .find(rec =>
          (rec.guidelineId === item.itemId &&
            rec.batchOrder === this.activeSubmission.order &&
            rec.isNewRec &&
            rec.actionType === CommentActionTypes.ADD))
    },
    logSubmissionUpdateAction (item) {
      if (!this.isNewRecord(item)) {
        this.logSubmissionAction({
          actionType: CommentActionTypes.UPDATE,
          commentObject: CommentObject.COMMENT,
          guidelineId: item.itemId,
        })
      }
    },
    async submit () {
      await this.updateCurrentTenantSubmission({})
      formUtil.resetDirty()
      await this.loadTenantProject(this.$router.currentRoute.params.id)
    },
    async saveWithEditingMode (item) {
      this.logSubmissionUpdateAction(item)
      await this.submit()
    },
    async save (item) {
      await this.saveWithEditingMode(item)
      this.turnAllEditToView()
    },
    // Tab-leave sets suppressAutosave before blur; later page blurs must persist.
    async onBlurSave (item) {
      if (this.suppressAutosave) {
        clearTimeout(this.suppressAutosaveTimeout)
        this.suppressAutosave = false
        this.suppressAutosaveTimeout = null
        this.turnAllEditToView()
        return
      }
      await this.save(item)
    },
    showSeq (line) {
      if (line.type === techCommentLineTypes.USER) {
        return ''
      } else {
        return line.order + 1
      }
    },
    openDelModalForLine (section, line) {
      const confirmAction = async (section, line) => {
        this.deleteLine(section, line)
        await this.updateCurrentTenantSubmission({})
        this.loadTenantProject(this.$router.currentRoute.params.id)
      }
      this.$refs.deleteCommentModal.open(confirmAction, [section, line])
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~styles/tenants/guideline";

.section-desc {
  color: #878787;
  font-style: italic;
  margin-bottom: 15px;
}

.collapse {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  & ::v-deep .title-container {
    width: calc(100% - 36px);
    height: auto;
    margin-bottom: 5px;
    flex-grow: 1;
    &.is-editing {
      flex-grow: 0;
    }
  }
  & ::v-deep .additional-container {
    width: 36px;
    display: flex;
    justify-content: center;
    &.hidden {
      width: 0;
    }
  }
  & ::v-deep .content.with-border {
    width: 100%;
    border: 0;
    margin-bottom: 15px;
    .content-box {
      margin-top: 5px;
    }
  }
}

.section-title {
  &-box {
    font-size: 16px;
    font-weight: bold;
    font-family: Verdana;
    padding: 10px 0;
    margin-right: 5px;
  }
  &-editing {
    display: flex;
    margin-top: 10px;
  }
  &-order {
    width: 13%;
    margin-right: 10px;
  }
}

.update-history__text {
  color: #878787;
  font-size: 12px;
  font-weight: normal;
  font-style: italic;
  text-align: right;
  margin-top: 10px;
}
</style>
